/**
 * Restaurants API client.
 */
import client from './client';

export interface Restaurant {
  id: number;
  business_name: string;
  store_type: 'restaurant';
  description?: string | null;
  logo_url?: string | null;
  cover_photo_url?: string | null;
  address?: string | null;
  city?: string | null;
  rating: number;
  is_open: boolean;
  distance_km?: number | null;
  opening_hours?: string | null;
  closing_hours?: string | null;
}

export interface MenuItem {
  id: number;
  store_id: number;
  category_id: number;
  name: string;
  description?: string | null;
  price: number;
  image_url?: string | null;
  thumbnail_url?: string | null;
  is_veg: boolean;
  is_available: boolean;
}

export interface MenuCategory {
  id: number;
  store_id: number;
  name: string;
  display_order: number;
  items: MenuItem[];
}

export interface RestaurantMenu {
  restaurant: Restaurant;
  categories: MenuCategory[];
}

export interface MenuItemHit {
  id: number;
  name: string;
  image_url?: string | null;
  thumbnail_url?: string | null;
  price: number;
  is_veg: boolean;
  store_id: number;
  store_name?: string | null;
  category_name?: string | null;
}

export const restaurantsAPI = {
  async list(params: { lat?: number; lng?: number; openOnly?: boolean; limit?: number } = {}): Promise<Restaurant[]> {
    const res = await client.get<Restaurant[]>('/api/restaurants', {
      params: {
        lat: params.lat,
        lng: params.lng,
        open_only: params.openOnly,
        limit: params.limit ?? 50,
      },
    });
    return res.data;
  },

  async getMenu(restaurantId: number): Promise<RestaurantMenu> {
    const res = await client.get<RestaurantMenu>(`/api/restaurants/${restaurantId}/menu`);
    return res.data;
  },

  async searchMenuItems(q: string, limit: number = 20): Promise<MenuItemHit[]> {
    const res = await client.get<MenuItemHit[]>('/api/menu-items/search', {
      params: { q, limit },
    });
    return res.data;
  },

  /**
   * Place a restaurant order. Restaurant carts live entirely client-side
   * (zustand + localStorage), so we POST the lines explicitly. The server
   * recomputes pricing from the trusted MenuItem rows — the `price` we
   * may have cached client-side is ignored.
   *
   * Returns the created Order; caller should then `restaurantCart.clear()`
   * and navigate to /orders/{id}/confirmation.
   */
  async createOrder(payload: {
    restaurant_id: number;
    lines: Array<{ menu_item_id: number; quantity: number }>;
    delivery_address: string;
    delivery_latitude?: number | null;
    delivery_longitude?: number | null;
    special_instructions?: string | null;
  }): Promise<{ id: number; total_amount: number; status: string }> {
    const res = await client.post('/api/orders/restaurant', payload);
    return res.data;
  },
};

